import { createContext, useContext, useState, ReactNode } from 'react';

interface JobSearchPanelContextType {
  isOpen: boolean;
  isMinimized: boolean;
  openPanel: () => void;
  closePanel: () => void;
  toggleMinimize: () => void;
  autoFillJob: (description: string) => void;
  onAutoFillCallback?: (description: string) => void;
}

const JobSearchPanelContext = createContext<JobSearchPanelContextType | undefined>(undefined);

export function JobSearchPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem('jobSearchPanelOpen');
    return stored ? JSON.parse(stored) : false;
  });
  const [isMinimized, setIsMinimized] = useState(() => {
    const stored = localStorage.getItem('jobSearchPanelMinimized');
    return stored ? JSON.parse(stored) : false;
  });
  const [onAutoFillCallback, setOnAutoFillCallback] = useState<((description: string) => void) | undefined>();

  const openPanel = () => {
    setIsOpen(true);
    localStorage.setItem('jobSearchPanelOpen', 'true');
  };

  const closePanel = () => {
    setIsOpen(false);
    localStorage.setItem('jobSearchPanelOpen', 'false');
  };

  const toggleMinimize = () => {
    const newState = !isMinimized;
    setIsMinimized(newState);
    localStorage.setItem('jobSearchPanelMinimized', JSON.stringify(newState));
  };

  const autoFillJob = (description: string) => {
    if (onAutoFillCallback) {
      onAutoFillCallback(description);
    }
  };

  return (
    <JobSearchPanelContext.Provider
      value={{
        isOpen,
        isMinimized,
        openPanel,
        closePanel,
        toggleMinimize,
        autoFillJob,
        onAutoFillCallback
      }}
    >
      {children}
    </JobSearchPanelContext.Provider>
  );
}

export function useJobSearchPanel() {
  const context = useContext(JobSearchPanelContext);
  if (!context) {
    throw new Error('useJobSearchPanel must be used within JobSearchPanelProvider');
  }
  return context;
}
